import { MapViewportHospitalView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createHospitalFeatureLayer } from '../helpers/feature-layers';
import { createHospitalGraphic } from '../helpers/graphics';
import { areLatLngsEqual } from '../../location/helpers';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { EsriMapEntityTypeEnum } from '../types';

type HospitalFeatureLayerProps = {
    mapViewportHospitals: MapViewportHospitalView[];
    selectedHospitalId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    webMap?: WebMap;
};

export const useHospitalFeatureLayer = ({
    mapViewportHospitals,
    selectedHospitalId,
    selectedMapLedgerEntityType,
    webMap,
}: HospitalFeatureLayerProps) => {
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.HOSPITALS>(
        EntityIdToObjectIdMaps.HOSPITALS
    );
    const [hospitalsLayer, setHospitalsLayer] = useState<FeatureLayer>();
    const prevSelectedHospitalId = usePreviousLayer(selectedHospitalId, hospitalsLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        hospitalsLayer
    );

    // hospital needs to be updated:
    // if selectedMapLedgerEntityType changed from HOSPITAL or to HOSPITAL
    // if hospital lat/lng changed
    // if selectedHospitalId differs from prevSelectedHospitalId
    const didHospitalGraphicChange = useCallback(
        (currHospital: MapViewportHospitalView, prevHospital: MapViewportHospitalView) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.HOSPITAL ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.HOSPITAL);

            return (
                isEntityTypeChanged ||
                !areLatLngsEqual(currHospital.geoLocation, prevHospital.geoLocation) ||
                (selectedHospitalId !== prevSelectedHospitalId &&
                    (currHospital.entityId === selectedHospitalId ||
                        currHospital.entityId === prevSelectedHospitalId))
            );
        },
        [
            prevSelectedHospitalId,
            selectedHospitalId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createHospitalGraphics = useCallback(
        (mapViewportHospitals: MapViewportHospitalView[]) => {
            const hospitalGraphics: Graphic[] = [];
            for (const mapViewportHospital of mapViewportHospitals) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    mapViewportHospital.entityId
                );
                const hospitalGraphic = createHospitalGraphic({
                    objectId,
                    mapViewportHospital,
                    selectedHospitalId,
                    selectedMapLedgerEntityType,
                });
                if (hospitalGraphic) {
                    hospitalGraphics.push(hospitalGraphic);
                }
            }
            return hospitalGraphics;
        },
        [selectedHospitalId, selectedMapLedgerEntityType]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const hospitalFeatureLayer = createHospitalFeatureLayer();
            setHospitalsLayer(hospitalFeatureLayer);
            webMap.layers.add(hospitalFeatureLayer);
        }
    }, [webMap]);

    useLayerControl<MapViewportHospitalView>({
        layer: hospitalsLayer,
        currItems: mapViewportHospitals,
        didItemChange: didHospitalGraphicChange,
        createGraphics: createHospitalGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { hospitalsLayer };
};
